import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldCheck, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Missing reset token. Please use the link from your email.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Reset failed');
        if (data.detail?.includes('expired')) setError(data.detail);
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <div className="pt-32 pb-24 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-outfit text-3xl font-semibold text-white mb-2">Set new password</h1>
            <p className="text-slate-300">Choose a strong, memorable password</p>
          </div>

          <div className="glass-card p-8">
            {error && !done ? (
              <div className="text-center space-y-4" data-testid="reset-error">
                <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                <p className="text-slate-300">{error}</p>
                <Link to="/forgot-password">
                  <Button className="btn-primary mt-4" data-testid="request-new-link-btn">Request a new link</Button>
                </Link>
              </div>
            ) : done ? (
              <div className="text-center space-y-4" data-testid="reset-success">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
                <h2 className="text-xl font-semibold text-white">Password updated!</h2>
                <p className="text-slate-300">You can now log in with your new password.</p>
                <Button onClick={() => navigate('/login')} className="btn-primary mt-4" data-testid="go-to-login-btn">
                  Go to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">New password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="input-dark pr-10"
                      data-testid="reset-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-slate-300">Confirm password</Label>
                  <Input
                    id="confirm"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="input-dark"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="reset-submit-btn">
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
